import { recordError } from "@/services/record-error";

import { clsx } from "clsx";
import { format, isValid } from "date-fns";
import { fr } from "date-fns/locale";
import React from "react";
import { twMerge } from "tailwind-merge";

export {
  ACTION_CONFIG,
  TITLE_OPTIONS,
  TITLE_REQUIRED_DOCUMENTS,
} from "./constants";
export { COUNTRY_OPTIONS, getDateInputValue } from "./customer";
export {
  buildPublicBookingPath,
  buildPublicTherapistPath,
} from "./public-therapist-path";
export {
  addDaysToDateValue,
  APP_TIMEZONE,
  formatDateValueShort,
  getDatePartsInTimezone,
  getDayColumns,
  getDayColumnsFromStart,
  getTodayDateValue,
} from "./timezone";

export function cn(...inputs) {
  return twMerge(clsx(inputs));
}

export const getInitials = (firstName, lastName) => {
  if (!firstName && !lastName) return "";
  if (!firstName) return `${lastName?.[0] || ""}`.toUpperCase();
  if (!lastName) return `${firstName?.[0] || ""}`.toUpperCase();
  return `${firstName[0]}${lastName[0]}`.toUpperCase();
};

export const formatDateTime = (value) => {
  if (!value) return "-";
  const date = new Date(value);
  if (!isValid(date)) return "-";
  return format(date, "dd/MM/yyyy", { locale: fr });
};

export const getAvatarUrl = (avatar) => {
  if (!avatar) return "";
  return avatar.url || avatar;
};

export function getMainOffice(offices) {
  if (!Array.isArray(offices) || offices.length === 0) return null;

  const defaultOffice = offices.find((office) => office?.is_default);
  if (defaultOffice) return defaultOffice;

  return offices[0] || null;
}

export function normalizePhoneFR(phone) {
  const value = String(phone || "").trim();
  if (!value) return "";
  if (value.includes("@")) return "";

  const compact = value.replace(/[^\d+]/g, "");
  if (!compact) return "";

  if (compact.startsWith("+")) {
    const normalized = `+${compact.slice(1).replace(/\D/g, "")}`;
    if (!/^\+[1-9][0-9]{6,14}$/.test(normalized)) return "";
    return normalized;
  }

  const digits = compact.replace(/\D/g, "");
  if (!digits) return "";
  if (/^00[1-9][0-9]{6,14}$/.test(digits)) return `+${digits.slice(2)}`;
  if (/^0[67][0-9]{8}$/.test(digits)) return `+33${digits.slice(1)}`;
  if (/^[1-9][0-9]{6,14}$/.test(digits)) return `+${digits}`;
  return "";
}

export function getApiErrorMessage(
  error,
  fallbackMessage = "Une erreur est survenue."
) {
  if (
    typeof error?.message === "string" &&
    error.message.trim().startsWith("<!DOCTYPE html>")
  ) {
    return fallbackMessage;
  }
  if (error?.code === "NETWORK_ERROR") {
    return "Connexion impossible. Vérifiez votre réseau puis réessayez.";
  }
  if (error?.code === "REQUEST_TIMEOUT") {
    return "Cette action demande un peu plus de temps. Vous pourrez réessayer dans quelques instants.";
  }
  return error?.message || error?.code || fallbackMessage;
}

export function isUnauthorizedError(error) {
  if (!error) return false;
  if (error?.status === 401) return true;
  if (error?.code === "UNAUTHORIZED") return true;

  const message = typeof error === "string" ? error : error?.message;
  if (typeof message !== "string") return false;

  return (
    message.trim() === "UNAUTHORIZED" ||
    message.trim() === "Vous n'êtes pas autorisé." ||
    message.trim() === "Vous n'êtes pas autorisé"
  );
}

export const sanitizeExternalUrl = (value) => {
  if (!value) return "";
  const normalized = String(value).trim();
  if (!normalized) return "";
  try {
    const parsed = new URL(normalized);
    if (parsed.protocol !== "http:" && parsed.protocol !== "https:") return "";
    return parsed.toString();
  } catch (error) {
    recordError(error, {
      source: "utils/index",
      operation: "sanitizeExternalUrl",
    });
    return "";
  }
};

export function useMediaQuery(query) {
  const [value, setValue] = React.useState(false);

  React.useEffect(() => {
    function onChange(event) {
      setValue(event.matches);
    }

    const result = matchMedia(query);
    result.addEventListener("change", onChange);
    setValue(result.matches);

    return () => result.removeEventListener("change", onChange);
  }, [query]);

  return value;
}

export function useOnClickOutside(ref, handler) {
  React.useEffect(() => {
    const listener = (event) => {
      if (!ref.current || ref.current.contains(event.target)) return;
      handler(event);
    };

    document.addEventListener("mousedown", listener);
    document.addEventListener("touchstart", listener, { passive: true });

    return () => {
      document.removeEventListener("mousedown", listener);
      document.removeEventListener("touchstart", listener);
    };
  }, [ref, handler]);
}
